import React, { useState, useRef, useCallback, useEffect } from 'react';
import './PromptEmailEditor.css';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';

let blockIdCounter = 0;
const newId = () => ++blockIdCounter;

const BLOCK_TYPES = {
    TITULO: 'titulo',
    PARAGRAFO: 'paragrafo',
    LINK: 'link',
};

const DADOS_FIELDS = [
    { key: 'cliente_nome', label: 'Nome' },
    { key: 'cliente_email', label: 'Email' },
    { key: 'cliente_celular', label: 'Celular' },
    { key: 'cliente_cpf_cnpj', label: 'CPF/CNPJ' },
    { key: 'cliente_endereco', label: 'Endereço' },
    { key: 'cliente_cidade', label: 'Cidade' },
    { key: 'cliente_estado', label: 'Estado' },
    { key: 'cliente_cep', label: 'CEP' },
];

const BASE_URLS_PROMPT = [
    `${BASE_URL}/PromptEmail`,
    `${BASE_URL}/PromptEmail`,
    '/api/PromptEmail'
];

const promptApiCall = async (endpoint = '', method = 'GET', data = null) => {
    let lastError;
    for (const baseUrl of BASE_URLS_PROMPT) {
        try {
            const url = endpoint ? `${baseUrl}/${endpoint}` : baseUrl;
            const config = {
                method,
                headers: { 'Content-Type': 'application/json' },
            };
            const token = getAuthToken();
            if (token) config.headers['Authorization'] = `Bearer ${token}`;
            if (data && method !== 'GET') config.body = JSON.stringify(data);
            const response = await fetch(url, config);
            if (!response.ok) throw new Error(`Erro ${response.status}: ${response.statusText}`);
            const text = await response.text();
            return text ? JSON.parse(text) : null;
        } catch (error) {
            lastError = error;
            continue;
        }
    }
    throw lastError;
};

function createBlock(type) {
    const id = newId();
    switch (type) {
        case BLOCK_TYPES.TITULO:
            return { id, type, html: '', align: 'left' };
        case BLOCK_TYPES.PARAGRAFO:
            return { id, type, html: '' };
        case BLOCK_TYPES.LINK:
            return { id, type, text: 'Link', url: '#', color: '#eb8500', textColor: '#ffffff', align: 'left' };
        default:
            return { id, type: BLOCK_TYPES.PARAGRAFO, html: '' };
    }
}

// --- Configurações globais (fonte, tamanho, negrito e cor de TODOS os blocos) ---
// Ficam no estilo do bloco; a formatação feita num bloco pelo menu de cima fica DENTRO do texto
// (negrito, cor, fonte), então sempre vale por cima das globais e não é apagada quando as globais mudam.
const FONTES = ["Arial", "Times New Roman", "Courier New", "Georgia", "Verdana", "Tahoma", "Trebuchet MS", "Palatino Linotype", "Lucida Sans Unicode", "Calibri", "Arial Black", "Impact", "Garamond", "Book Antiqua", "Cambria"];
// Tamanhos da barra de cima (valor do editor -> pontos)
const TAMANHOS_BARRA = [
    { valor: '1', pt: 8 }, { valor: '2', pt: 10 }, { valor: '3', pt: 12 }, { valor: '4', pt: 14 },
    { valor: '5', pt: 18 }, { valor: '6', pt: 24 }, { valor: '7', pt: 36 },
];

const ESTILO_GLOBAL_PADRAO ={ fonte: '', tamanho: '', negrito: false, cor: '' };
const TAMANHOS_GLOBAIS = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24];
const DIFERENCA_TAMANHO_TITULO = 4; // o título fica sempre um pouco maior que o texto

function estiloDoBloco(tipo, g) {
    const s = {};
    if (g.fonte) s.fontFamily = g.fonte;
    if (tipo === BLOCK_TYPES.LINK) {
        if (g.negrito) s.fontWeight = 700;
        return s;
    }
    if (g.tamanho) s.fontSize = (Number(g.tamanho) + (tipo === BLOCK_TYPES.TITULO ? DIFERENCA_TAMANHO_TITULO : 0)) + 'pt';
    if (g.negrito) s.fontWeight = 700;
    if (g.cor) s.color = g.cor;
    return s;
}

function estiloParaCss(s) {
    const css = [];
    if (s.fontFamily) css.push('font-family:' + s.fontFamily);
    if (s.fontSize) css.push('font-size:' + s.fontSize);
    if (s.fontWeight) css.push('font-weight:' + s.fontWeight);
    if (s.color) css.push('color:' + s.color);
    return css.length ? css.join(';') + ';' : '';
}

// --- HTML Serializer: blocks -> HTML with data attributes for reconstruction ---
function serializeBlocksToHtml(blocks, blockRefs, estiloGlobal = ESTILO_GLOBAL_PADRAO) {
    let html = '';
    blocks.forEach(b => {
        switch (b.type) {
            case BLOCK_TYPES.TITULO: {
                const el = blockRefs.current[b.id];
                const c = el ? el.innerHTML : (b.html || '');
                const g = estiloParaCss(estiloDoBloco(b.type, estiloGlobal));
                const tamanho = estiloGlobal.tamanho ? '' : 'font-size:14pt;';
                html += '<div data-block-type="titulo" style="margin:0 0 10px;' + tamanho + 'text-align:left;' + g + '">' + c + '</div>';
                break;
            }
            case BLOCK_TYPES.PARAGRAFO: {
                const el = blockRefs.current[b.id];
                const c = el ? el.innerHTML : (b.html || '');
                html += '<div data-block-type="paragrafo" style="margin:0 0 10px;line-height:1.6;' + estiloParaCss(estiloDoBloco(b.type, estiloGlobal)) + '">' + c + '</div>';
                break;
            }
            case BLOCK_TYPES.LINK:
                html += '<div data-block-type="link" data-link-text="' + (b.text || 'Link').replace(/"/g, '&quot;') + '" data-link-url="' + (b.url || '#').replace(/"/g, '&quot;') + '" data-link-color="' + (b.color || '#eb8500') + '" data-link-textcolor="' + (b.textColor || '#ffffff') + '"><p style="text-align:left;margin:0 0 10px;"><a href="' + (b.url || '#') + '" style="background:' + (b.color || '#eb8500') + ';color:' + (b.textColor || '#fff') + ';padding:10px 24px;border-radius:6px;text-decoration:none;display:inline-block;' + estiloParaCss(estiloDoBloco(BLOCK_TYPES.LINK, estiloGlobal)) + '">' + (b.text || 'Link') + '</a></p></div>';
                break;
            default: break;
        }
    });
    // Replace inline button elements with proper button tags
    html = html.replace(/<button[^>]*class="prompt-email-dados-inline-btn"[^>]*data-dados="([^"]*)"[^>]*>([^<]*)<\/button>/g,
        '<button class="dados-btn" data-dados="$1">$2</button>');
    // Guarda as configurações globais para reabrir o template igual
    const temGlobal = estiloGlobal.fonte || estiloGlobal.tamanho || estiloGlobal.negrito || estiloGlobal.cor;
    if (temGlobal) {
        html = '<div data-estilo-global="' + encodeURIComponent(JSON.stringify(estiloGlobal)) + '">' + html + '</div>';
    }
    return html;
}

function lerEstiloGlobal(html) {
    const m = /data-estilo-global="([^"]*)"/.exec(html || '');
    if (!m) return ESTILO_GLOBAL_PADRAO;
    try {
        return { ...ESTILO_GLOBAL_PADRAO, ...JSON.parse(decodeURIComponent(m[1])) };
    } catch {
        return ESTILO_GLOBAL_PADRAO;
    }
}

// --- HTML Deserializer: HTML -> blocks ---
function deserializeHtmlToBlocks(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const blockDivs = doc.querySelectorAll('[data-block-type]');
    const newBlocks = [];

    blockDivs.forEach(div => {
        const type = div.getAttribute('data-block-type');
        const id = newId();
        if (type === 'titulo') {
            const h2 = div.querySelector('h2');
            let innerHtml = h2 ? h2.innerHTML : div.innerHTML;
            // Restore dados buttons
            innerHtml = restoreDadosButtons(innerHtml);
            newBlocks.push({ id, type: BLOCK_TYPES.TITULO, html: innerHtml, align: 'left' });
        } else if (type === 'paragrafo') {
            const p = div.querySelector('p');
            let innerHtml = p ? p.innerHTML : div.innerHTML;
            innerHtml = restoreDadosButtons(innerHtml);
            newBlocks.push({ id, type: BLOCK_TYPES.PARAGRAFO, html: innerHtml });
        } else if (type === 'link') {
            newBlocks.push({
                id,
                type: BLOCK_TYPES.LINK,
                text: div.getAttribute('data-link-text') || 'Link',
                url: div.getAttribute('data-link-url') || '#',
                color: div.getAttribute('data-link-color') || '#eb8500',
                textColor: div.getAttribute('data-link-textcolor') || '#ffffff',
                align: 'left',
            });
        }
    });

    return newBlocks.length > 0 ? newBlocks : [createBlock(BLOCK_TYPES.PARAGRAFO), createBlock(BLOCK_TYPES.PARAGRAFO)];
}

function restoreDadosButtons(html) {
    // Convert <button class="dados-btn" data-dados="key">label</button> back to inline editor buttons
    return html.replace(/<button[^>]*class="dados-btn"[^>]*data-dados="([^"]*)"[^>]*>([^<]*)<\/button>/g,
        '<button data-dados="$1" contenteditable="false" class="prompt-email-dados-inline-btn" type="button">$2</button>');
}

function PromptEmailEditor({ onClose, onSave }) {
    const [blocks, setBlocks] = useState([createBlock(BLOCK_TYPES.TITULO)]);
    const [selectedBlockId, setSelectedBlockId] = useState(null);
    const [sidebarMode, setSidebarMode] = useState('blocks');
    const [editingBlock, setEditingBlock] = useState(null);
    const [templateName, setTemplateName] = useState('');
    const [draggedBlockId, setDraggedBlockId] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [dragSide, setDragSide] = useState(null);
    const [headerTextColor, setHeaderTextColor] = useState('#333333');
    const [estiloGlobal, setEstiloGlobal] = useState(ESTILO_GLOBAL_PADRAO);
    const [avisoFormatacao, setAvisoFormatacao] = useState('');
    // Barra de formatação de cima: só aparece com um título/parágrafo selecionado (ou durante o tour da Ajuda)
    const [fonteBarra, setFonteBarra] = useState('Arial');
    const [tamanhoBarra, setTamanhoBarra] = useState('3');
    const [forcarBarra, setForcarBarra] = useState(false);
    const blocoSelecionado = blocks.find(b => b.id === selectedBlockId);
    const barraVisivel = forcarBarra || (!!blocoSelecionado && blocoSelecionado.type !== BLOCK_TYPES.LINK);
    const [showExitPopup, setShowExitPopup] = useState(false);
    const [editingTemplateId, setEditingTemplateId] = useState(null);
    const [showLoadPopup, setShowLoadPopup] = useState(false);
    const [savedTemplates, setSavedTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);

    const blockRefs = useRef({});
    const activeEditableRef = useRef(null);
    const focusedBlockIdRef = useRef(null);
    const savedSelectionRef = useRef(null);

    // ---- Block CRUD ----
    const addBlock = useCallback((type, atIndex) => {
        const block = createBlock(type);
        setBlocks(prev => {
            const idx = atIndex !== undefined ? atIndex : prev.length;
            const next = [...prev];
            next.splice(idx, 0, block);
            return next;
        });
        setSelectedBlockId(block.id);
        if (type === BLOCK_TYPES.LINK) {
            setEditingBlock(block);
            setSidebarMode('edit-link');
        }
        setTimeout(() => {
            const el = blockRefs.current[block.id];
            if (el && el.focus) el.focus();
        }, 50);
        return block;
    }, []);

    const updateBlock = useCallback((id, updates) => {
        setBlocks(prev => prev.map(b => b.id === id ? { ...b, ...updates } : b));
        if (editingBlock && editingBlock.id === id) {
            setEditingBlock(prev => prev ? { ...prev, ...updates } : prev);
        }
    }, [editingBlock]);

    const deleteBlock = useCallback((id) => {
        setBlocks(prev => prev.filter(b => b.id !== id));
        if (selectedBlockId === id) setSelectedBlockId(null);
        if (editingBlock && editingBlock.id === id) {
            setEditingBlock(null);
            setSidebarMode('blocks');
        }
        delete blockRefs.current[id];
    }, [selectedBlockId, editingBlock]);

    const moveBlock = useCallback((fromIndex, toIndex) => {
        setBlocks(prev => {
            const next = [...prev];
            const [moved] = next.splice(fromIndex, 1);
            const adj = toIndex > fromIndex ? toIndex - 1 : toIndex;
            next.splice(adj, 0, moved);
            return next;
        });
    }, []);

    // ---- Selection & Format Toolbar ----
    const saveSelection = () => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
            savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
        }
        lerFormatacaoDaSelecao();
    };

    // A barra de cima mostra a formatação de onde está o cursor (ou do bloco selecionado)
    const lerFormatacaoDaSelecao = () => {
        const bloco = blocks.find(b => b.id === selectedBlockId);
        const elBloco = bloco ? blockRefs.current[bloco.id] : null;
        if (!elBloco) return;
        const sel = window.getSelection();
        let alvo = elBloco;
        if (sel && sel.anchorNode && elBloco.contains(sel.anchorNode)) {
            alvo = sel.anchorNode.nodeType === 3 ? sel.anchorNode.parentElement : sel.anchorNode;
        }
        const estilo = window.getComputedStyle(alvo);
        const familia = (estilo.fontFamily || '').split(',')[0].replace(/["']/g, '').trim();
        setFonteBarra(FONTES.find(f => f.toLowerCase() === familia.toLowerCase()) || 'Arial');
        const pt = parseFloat(estilo.fontSize) * 0.75;
        const mais_proximo = TAMANHOS_BARRA.reduce((a, b) => Math.abs(b.pt - pt) < Math.abs(a.pt - pt) ? b : a);
        setTamanhoBarra(mais_proximo.valor);
        const rgb = (estilo.color || '').match(/\d+/g);
        if (rgb && rgb.length >= 3) {
            setHeaderTextColor('#' + rgb.slice(0, 3).map(n => Number(n).toString(16).padStart(2, '0')).join(''));
        }
    };

    // Ao trocar de bloco, a barra desce mostrando a formatação dele
    useEffect(() => {
        lerFormatacaoDaSelecao();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBlockId]);

    // Clicar fora dos blocos (e fora da barra) recolhe a barra
    const handleCliqueFora = (e) => {
        if (!e.target.closest('.prompt-email-block, .prompt-email-format-toolbar')) {
            setSelectedBlockId(null);
        }
    };

    const restoreSelection = () => {
        const r = savedSelectionRef.current;
        if (!r) return;
        try {
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(r.cloneRange());
        } catch (e) {
            const el = activeEditableRef.current;
            if (el) {
                const range = document.createRange();
                range.selectNodeContents(el);
                const sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(range);
            }
        }
    };

    // Menu de cima: formata SÓ o bloco selecionado (o texto marcado nele, ou o bloco inteiro se nada estiver marcado).
    // Para mudar todos os blocos de uma vez, use "Configurações Globais" na barra da esquerda.
    const execFormat = (command, value) => {
        const bloco = blocks.find(b => b.id === selectedBlockId);
        const el = bloco && bloco.type !== BLOCK_TYPES.LINK ? blockRefs.current[bloco.id] : null;
        if (!el) {
            setAvisoFormatacao('Clique em um título ou parágrafo para formatar. Para mudar todos os blocos, use "Configurações Globais".');
            setTimeout(() => setAvisoFormatacao(''), 4000);
            return;
        }
        restoreSelection();
        const sel = window.getSelection();
        const selecaoNoBloco = sel && sel.rangeCount > 0 && el.contains(sel.getRangeAt(0).commonAncestorContainer);
        if (!selecaoNoBloco || sel.toString().length === 0) {
            el.focus();
            const range = document.createRange();
            range.selectNodeContents(el);
            if (sel) { sel.removeAllRanges(); sel.addRange(range); }
        }
        document.execCommand(command, false, value || null);
        updateBlock(bloco.id, { html: el.innerHTML });
    };

    // ---- Ajuda: avisa o botão de Ajuda que o editor está aberto (para o "Resumo deste formulário") ----
    useEffect(() => {
        window.dispatchEvent(new CustomEvent('prompt-email-popup-state', { detail: { formularioAberto: true } }));
        return () => window.dispatchEvent(new CustomEvent('prompt-email-popup-state', { detail: { formularioAberto: false } }));
    }, []);

    // O tour da Ajuda abre os painéis da esquerda para explicar cada um
    useEffect(() => {
        const handler = (e) => {
            const acao = e.detail;
            if (acao === 'abrir-link') {
                const link = blocks.find(b => b.type === BLOCK_TYPES.LINK);
                // Sem bloco de link no template: mostra um exemplo só para a explicação (não entra no e-mail)
                setEditingBlock(link ? { ...link } : { ...createBlock(BLOCK_TYPES.LINK), id: -1 });
                setSidebarMode('edit-link');
            } else if (acao === 'abrir-globais') {
                setForcarBarra(false);
                setSidebarMode('globais');
            } else if (acao === 'mostrar-barra') {
                setSidebarMode('blocks');
                setEditingBlock(null);
                setForcarBarra(true);
            } else if (acao === 'voltar-blocos') {
                setForcarBarra(false);
                setSidebarMode('blocks');
                setEditingBlock(null);
            }
        };
        window.addEventListener('prompt-email-tour-acao', handler);
        return () => window.removeEventListener('prompt-email-tour-acao', handler);
    }, [blocks]);

    // ---- Drag & Drop ----
    const handleSidebarDragStart = (e, type) => {
        e.dataTransfer.setData('text/plain', 'sidebar-' + type);
        e.dataTransfer.effectAllowed = 'copy';
    };

    const handleDadosDragStart = (e, field) => {
        e.dataTransfer.setData('text/plain', 'dados-' + field.key + '|' + field.label);
        e.dataTransfer.effectAllowed = 'copy';
    };

    const handleBlockDragStart = (e, blockId) => {
        e.stopPropagation();
        setDraggedBlockId(blockId);
        e.dataTransfer.setData('text/plain', 'block-' + blockId);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleBlockDragOver = (e, index) => {
        if (e.dataTransfer.types.includes('text/plain')) e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const side = e.clientY < rect.top + rect.height / 2 ? 'top' : 'bottom';
        setDragOverIndex(index);
        setDragSide(side);
    };

    const handleBlockDragLeave = () => { setDragOverIndex(null); setDragSide(null); };

    const handleBlockDrop = (e, dropIndex) => {
        e.preventDefault();
        const data = e.dataTransfer.getData('text/plain');
        if (data.startsWith('dados-')) return; // handled by contentEditable drop
        const insertAt = dragSide === 'bottom' ? dropIndex + 1 : dropIndex;
        if (data.startsWith('sidebar-')) {
            addBlock(data.replace('sidebar-', ''), insertAt);
        } else if (data.startsWith('block-')) {
            const bid = parseInt(data.replace('block-', ''), 10);
            const fi = blocks.findIndex(b => b.id === bid);
            if (fi !== -1) moveBlock(fi, insertAt);
        }
        setDraggedBlockId(null); setDragOverIndex(null); setDragSide(null);
    };

    const handleCanvasDrop = (e) => {
        e.preventDefault();
        const data = e.dataTransfer.getData('text/plain');
        if (data.startsWith('sidebar-')) addBlock(data.replace('sidebar-', ''));
        setDraggedBlockId(null); setDragOverIndex(null); setDragSide(null);
    };

    // Insert dados BUTTON at caret inside a contentEditable (only paragraphs)
    const handleEditableDrop = (e, blockId) => {
        const data = e.dataTransfer.getData('text/plain');
        if (!data.startsWith('dados-')) return;
        e.preventDefault();
        e.stopPropagation();
        // Block dados drop on titulo blocks
        const targetBlock = blocks.find(b => b.id === blockId);
        if (targetBlock && targetBlock.type === BLOCK_TYPES.TITULO) return;
        const parts = data.replace('dados-', '').split('|');
        const fieldKey = parts[0];
        const fieldLabel = parts[1] || fieldKey;
        const el = blockRefs.current[blockId];
        if (!el) return;
        el.focus();
        // Position caret at exact drop point
        let range;
        if (document.caretRangeFromPoint) {
            range = document.caretRangeFromPoint(e.clientX, e.clientY);
        } else if (document.caretPositionFromPoint) {
            const pos = document.caretPositionFromPoint(e.clientX, e.clientY);
            if (pos) { range = document.createRange(); range.setStart(pos.offsetNode, pos.offset); range.collapse(true); }
        }
        if (range) {
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
        }
        // Create an actual <button> element inline
        const btn = document.createElement('button');
        btn.setAttribute('data-dados', fieldKey);
        btn.setAttribute('contenteditable', 'false');
        btn.className = 'prompt-email-dados-inline-btn';
        btn.textContent = fieldLabel;
        btn.type = 'button';
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
            const r = sel.getRangeAt(0);
            r.deleteContents();
            r.insertNode(btn);
            // Add a zero-width space after so caret can be placed after the button
            const spacer = document.createTextNode('\u200B');
            r.setStartAfter(btn);
            r.collapse(true);
            r.insertNode(spacer);
            r.setStartAfter(spacer);
            r.collapse(true);
            sel.removeAllRanges();
            sel.addRange(r);
        } else {
            el.appendChild(btn);
            el.appendChild(document.createTextNode('\u200B'));
        }
        updateBlock(blockId, { html: el.innerHTML });
    };

    // ---- HTML Generation ----
    const generateHTML = () => {
        return serializeBlocksToHtml(blocks, blockRefs, estiloGlobal);
    };

    const handleSave = async () => {
        if (!templateName.trim()) { alert('Por favor, dê um nome ao template.'); return; }
        const html = generateHTML();
        try {
            if (editingTemplateId) {
                // PUT - atualizar template existente
                const result = await promptApiCall(`${editingTemplateId}`, 'PUT', {
                    titulo: templateName,
                    templateHtml: html
                });
                alert('Template atualizado com sucesso!');
                if (onSave) onSave({ id: editingTemplateId, name: templateName, html, blocks });
            } else {
                // POST - criar novo template
                const result = await promptApiCall('', 'POST', {
                    titulo: templateName,
                    templateHtml: html
                });
                if (result && result.id) setEditingTemplateId(result.id);
                alert('Template salvo com sucesso!');
                if (onSave) onSave({ id: result?.id, name: templateName, html, blocks });
            }
        } catch (err) {
            console.error('Erro ao salvar template:', err);
            alert('Erro ao salvar template: ' + err.message);
        }
    };

    // ---- Carregar Templates ----
    const handleOpenLoadPopup = async () => {
        try {
            setLoadingTemplates(true);
            setShowLoadPopup(true);
            const templates = await promptApiCall();
            setSavedTemplates(Array.isArray(templates) ? templates : []);
        } catch (err) {
            console.error('Erro ao carregar templates:', err);
            setSavedTemplates([]);
        } finally {
            setLoadingTemplates(false);
        }
    };

    const handleSelectTemplate = async (template) => {
        try {
            const id = template.id || template.Id;
            const full = await promptApiCall(`${id}`);
            const htmlContent = full.templateHtml || full.TemplateHtml || '';
            const titulo = full.titulo || full.Titulo || '';

            // Deserialize HTML back to blocks
            const newBlocks = deserializeHtmlToBlocks(htmlContent);
            setEstiloGlobal(lerEstiloGlobal(htmlContent));

            // Reset blockRefs for new blocks
            blockRefs.current = {};

            setBlocks(newBlocks);
            setTemplateName(titulo);
            setEditingTemplateId(id);
            setShowLoadPopup(false);
            setSelectedBlockId(null);
            setSidebarMode('blocks');
            setEditingBlock(null);

            // Force re-render of contentEditable elements
            setTimeout(() => {
                newBlocks.forEach(b => {
                    const el = blockRefs.current[b.id];
                    if (el && b.html) el.innerHTML = b.html;
                });
            }, 100);
        } catch (err) {
            console.error('Erro ao carregar template:', err);
            alert('Erro ao carregar template: ' + err.message);
        }
    };

    // ---- Cancel / Exit ----
    const handleClose = () => {
        const hasContent = blocks.some(b => {
            if (b.type === BLOCK_TYPES.LINK) return b.text && b.text !== 'Link';
            const el = blockRefs.current[b.id];
            const html = el ? el.innerHTML : (b.html || '');
            return html.replace(/<br\s*\/?>/gi, '').trim().length > 0;
        }) || templateName.trim().length > 0;
        if (hasContent) {
            setShowExitPopup(true);
        } else {
            if (onClose) onClose();
        }
    };

    // ---- Block click ----
    const handleBlockClick = (block) => {
        setSelectedBlockId(block.id);
        if (block.type === BLOCK_TYPES.LINK) {
            setEditingBlock({ ...block });
            setSidebarMode('edit-link');
        } else if (block.type === BLOCK_TYPES.TITULO) {
            setEditingBlock({ ...block });
            setSidebarMode('edit-titulo');
        } else {
            if (sidebarMode === 'edit-link' || sidebarMode === 'edit-titulo') { setSidebarMode('blocks'); setEditingBlock(null); }
        }
    };

    // ---- Render block ----
    const renderBlock = (block, index) => {
        const isSelected = selectedBlockId === block.id;
        const isDragOver = dragOverIndex === index;
        let dragClass = '';
        if (isDragOver && dragSide === 'top') dragClass = 'drag-over-top';
        if (isDragOver && dragSide === 'bottom') dragClass = 'drag-over-bottom';

        const editableRef = (el) => {
            if (el && blockRefs.current[block.id] !== el) {
                blockRefs.current[block.id] = el;
                if (block.html) el.innerHTML = block.html;
            }
        };

        return (
            <div
                key={block.id}
                className={'prompt-email-block' + (isSelected ? ' selected' : '') + (dragClass ? ' ' + dragClass : '')}
                onClick={() => handleBlockClick(block)}
                onDragOver={(e) => handleBlockDragOver(e, index)}
                onDragLeave={handleBlockDragLeave}
                onDrop={(e) => handleBlockDrop(e, index)}
            >
                <div
                    className="prompt-email-block-drag"
                    title="Arrastar"
                    draggable
                    onDragStart={(e) => handleBlockDragStart(e, block.id)}
                >
                    <span className="drag-dot" /><span className="drag-dot" />
                    <span className="drag-dot" /><span className="drag-dot" />
                    <span className="drag-dot" /><span className="drag-dot" />
                </div>

                <div className="prompt-email-block-toolbar">
                    {index > 0 && <button title="Mover para cima" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); moveBlock(index, index - 1); }}>&#8593;</button>}
                    {index < blocks.length - 1 && <button title="Mover para baixo" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); moveBlock(index, index + 2); }}>&#8595;</button>}
                    <button title="Excluir" onMouseDown={(e) => e.preventDefault()} onClick={(e) => { e.stopPropagation(); deleteBlock(block.id); }}>&#215;</button>
                </div>

                {(block.type === BLOCK_TYPES.TITULO || block.type === BLOCK_TYPES.PARAGRAFO) && (
                    <div
                        ref={editableRef}
                        className={block.type === BLOCK_TYPES.TITULO ? 'prompt-email-titulo-input' : 'prompt-email-paragrafo-input'}
                        contentEditable
                        suppressContentEditableWarning
                        style={{ ...(block.type === BLOCK_TYPES.TITULO ? { textAlign: 'left' } : {}), ...estiloDoBloco(block.type, estiloGlobal) }}
                        data-placeholder={block.type === BLOCK_TYPES.TITULO ? 'Digite o titulo...' : 'Digite o texto do paragrafo...'}
                        onFocus={(e) => {
                            focusedBlockIdRef.current = block.id;
                            setSelectedBlockId(block.id);
                            activeEditableRef.current = e.currentTarget;
                        }}
                        onBlur={(e) => {
                            updateBlock(block.id, { html: e.currentTarget.innerHTML });
                            focusedBlockIdRef.current = null;
                        }}
                        onInput={(e) => updateBlock(block.id, { html: e.currentTarget.innerHTML })}
                        onSelect={saveSelection}
                        onKeyUp={saveSelection}
                        onMouseUp={saveSelection}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => handleEditableDrop(e, block.id)}
                        onClick={(e) => e.stopPropagation()}
                    />
                )}

                {block.type === BLOCK_TYPES.LINK && (
                    <div className="prompt-email-link-block" style={{ textAlign: 'left' }}>
                        <button
                            className="prompt-email-link-btn"
                            style={{ background: block.color || '#eb8500', color: block.textColor || '#fff', ...estiloDoBloco(block.type, estiloGlobal) }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setEditingBlock({ ...block });
                                setSidebarMode('edit-link');
                                setSelectedBlockId(block.id);
                            }}
                        >
                            {block.text || 'Link'}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    // ---- Render sidebar ----
    const renderSidebar = () => {
        if (sidebarMode === 'edit-link' && editingBlock) {
            return (
                <div className="prompt-email-sidebar-edit">
                    <div className="prompt-email-sidebar-edit-header">
                        <span className="prompt-email-sidebar-edit-title">Editar Link</span>
                        <button className="prompt-email-sidebar-edit-back" onClick={() => { setSidebarMode('blocks'); setEditingBlock(null); }}>Voltar</button>
                    </div>
                    <label id="pe-link-texto">Texto do botao
                        <input type="text" value={editingBlock.text || ''} placeholder="Link"
                            onChange={(e) => { const v = e.target.value; setEditingBlock(p => ({ ...p, text: v })); updateBlock(editingBlock.id, { text: v }); }} />
                    </label>
                    <label id="pe-link-url">URL de destino
                        <input type="text" value={editingBlock.url || ''} placeholder="https://..."
                            onChange={(e) => { const v = e.target.value; setEditingBlock(p => ({ ...p, url: v })); updateBlock(editingBlock.id, { url: v }); }} />
                    </label>
                    <div id="pe-link-cores" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <label>Cor do botao
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input type="color" value={editingBlock.color || '#eb8500'}
                                onChange={(e) => { const v = e.target.value; setEditingBlock(p => ({ ...p, color: v })); updateBlock(editingBlock.id, { color: v }); }}
                                style={{ width: 36, height: 36, border: 'none', cursor: 'pointer', background: 'transparent' }} />
                            <span style={{ color: '#aaa', fontSize: 12 }}>{editingBlock.color || '#eb8500'}</span>
                        </div>
                    </label>
                    <label>Cor do texto
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input type="color" value={editingBlock.textColor || '#ffffff'}
                                onChange={(e) => { const v = e.target.value; setEditingBlock(p => ({ ...p, textColor: v })); updateBlock(editingBlock.id, { textColor: v }); }}
                                style={{ width: 36, height: 36, border: 'none', cursor: 'pointer', background: 'transparent' }} />
                            <span style={{ color: '#aaa', fontSize: 12 }}>{editingBlock.textColor || '#ffffff'}</span>
                        </div>
                    </label>
                    </div>
                </div>
            );
        }

        if (sidebarMode === 'edit-titulo' && editingBlock) {
            return (
                <div className="prompt-email-sidebar-edit">
                    <div className="prompt-email-sidebar-edit-header">
                        <span className="prompt-email-sidebar-edit-title">Editar Título</span>
                        <button className="prompt-email-sidebar-edit-back" onClick={() => { setSidebarMode('blocks'); setEditingBlock(null); }}>Voltar</button>
                    </div>
                    <p style={{ color: '#aaa', fontSize: 11, marginTop: 16, lineHeight: 1.5 }}>
                        O título aceita apenas texto.<br/>Botões de dados só podem ser colocados em blocos de parágrafo.
                    </p>
                </div>
            );
        }

        if (sidebarMode === 'globais') {
            const mudar = (campo, valor) => setEstiloGlobal(prev => ({ ...prev, [campo]: valor }));
            return (
                <div className="prompt-email-sidebar-edit" id="pe-globais-painel">
                    <div className="prompt-email-sidebar-edit-header">
                        <span className="prompt-email-sidebar-edit-title">Configurações Globais</span>
                        <button className="prompt-email-sidebar-edit-back" onClick={() => setSidebarMode('blocks')}>Voltar</button>
                    </div>
                    <p style={{ color: '#aaa', fontSize: 12, margin: '0 0 4px', lineHeight: 1.6 }}>
                        Vale para todos os blocos, os atuais e os novos. O que você formatar num bloco pelo menu de cima continua valendo por cima destas configurações.
                    </p>
                    <label>Fonte
                        <select value={estiloGlobal.fonte} onChange={(e) => mudar('fonte', e.target.value)}>
                            <option value="">Padrão</option>
                            {FONTES.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                    </label>
                    <label>Tamanho do texto
                        <select value={estiloGlobal.tamanho} onChange={(e) => mudar('tamanho', e.target.value)}>
                            <option value="">Padrão</option>
                            {TAMANHOS_GLOBAIS.map(t => <option key={t} value={t}>{t}pt</option>)}
                        </select>
                    </label>
                    <label style={{ flexDirection: 'row', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!estiloGlobal.negrito} onChange={(e) => mudar('negrito', e.target.checked)} style={{ width: 16, height: 16 }} />
                        Negrito
                    </label>
                    <label>Cor do texto
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input type="color" value={estiloGlobal.cor || '#333333'} onChange={(e) => mudar('cor', e.target.value)}
                                style={{ width: 36, height: 36, border: 'none', cursor: 'pointer', background: 'transparent', padding: 0 }} />
                            <span style={{ color: '#aaa', fontSize: 12 }}>{estiloGlobal.cor || 'Padrão'}</span>
                        </div>
                    </label>
                    <button className="prompt-email-sidebar-edit-back" style={{ marginTop: 8 }} onClick={() => setEstiloGlobal(ESTILO_GLOBAL_PADRAO)}>
                        Restaurar padrão
                    </button>
                </div>
            );
        }

        if (sidebarMode === 'edit-dados') {
            return (
                <div className="prompt-email-sidebar-edit">
                    <div className="prompt-email-sidebar-edit-header">
                        <span className="prompt-email-sidebar-edit-title">Dados do Cliente</span>
                        <button className="prompt-email-sidebar-edit-back" onClick={() => setSidebarMode('blocks')}>Voltar</button>
                    </div>
                    <p style={{ color: '#aaa', fontSize: 12, margin: '0 0 12px', lineHeight: 1.6 }}>
                        Arraste um campo para dentro de qualquer bloco de texto. Um botao sera inserido no local exato.
                    </p>
                    <div className="prompt-email-dados-field-list">
                        {DADOS_FIELDS.map(f => (
                            <div
                                key={f.key}
                                className="prompt-email-dados-field-option"
                                draggable
                                onDragStart={(e) => handleDadosDragStart(e, f)}
                                style={{ cursor: 'grab' }}
                            >
                                <span className="prompt-email-dados-field-icon">&#8862;</span>
                                {f.label}
                                <span style={{ color: '#666', fontSize: 11, marginLeft: 'auto' }}>arrastar</span>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        return (
            <>
                <div className="prompt-email-sidebar-header">Blocos</div>
                <div className="prompt-email-sidebar-blocks" id="pe-blocos">
                    {[
                        { type: BLOCK_TYPES.TITULO, icon: 'T', iconClass: 'titulo', label: 'Titulo' },
                        { type: BLOCK_TYPES.PARAGRAFO, icon: '\u00B6', iconClass: 'paragrafo', label: 'Paragrafo' },
                        { type: BLOCK_TYPES.LINK, icon: '\uD83D\uDD17', iconClass: 'link', label: 'Links' },
                    ].map(({ type, icon, iconClass, label }) => (
                        <div key={type} className="prompt-email-sidebar-btn" draggable
                            onDragStart={(e) => handleSidebarDragStart(e, type)}
                            onClick={() => addBlock(type)}
                        >
                            <div className={'prompt-email-sidebar-btn-icon ' + iconClass}>{icon}</div>
                            {label}
                        </div>
                    ))}
                    <div className="prompt-email-sidebar-btn" onClick={() => setSidebarMode('edit-dados')}>
                        <div className="prompt-email-sidebar-btn-icon dados">{'\uD83D\uDC64'}</div>
                        Dados Clientes
                    </div>
                </div>

                <div className="prompt-email-sidebar-separador" aria-hidden="true" />

                <div className="prompt-email-sidebar-blocks">
                    <div className="prompt-email-sidebar-btn" onClick={() => setSidebarMode('globais')}>
                        <div className="prompt-email-sidebar-btn-icon global">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="3" />
                                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                            </svg>
                        </div>
                        Configurações Globais
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className="prompt-email-fullscreen" onMouseDown={handleCliqueFora}>
            {/* Topbar */}
            <div className="prompt-email-topbar">
                <div className="prompt-email-topbar-left">
                    <button className="prompt-email-btn-voltar" onClick={handleClose}>Cancelar</button>
                    <input className="prompt-email-name-input" placeholder="Nome do template..." value={templateName} onChange={(e) => setTemplateName(e.target.value)} />
                    <button className="prompt-email-btn-voltar" id="pe-btn-carregar" onClick={handleOpenLoadPopup}>Carregar Template</button>
                </div>
                <div className="prompt-email-topbar-right">
                    <button className="prompt-email-btn-salvar" onClick={handleSave}>
                        {editingTemplateId ? 'Atualizar Template' : 'Salvar Template'}
                    </button>
                </div>
            </div>

            <div className="prompt-email-body">
                {/* Sidebar */}
                <div className="prompt-email-sidebar">{renderSidebar()}</div>

                {/* Right panel: toolbar + canvas */}
                <div className="prompt-email-right-panel">

                    {/* Format toolbar (Word-like) */}
                    <div className={'prompt-email-format-toolbar' + (barraVisivel ? ' pef-visivel' : '')}>
                        <select className="pef-select" title="Fonte" value={fonteBarra}
                            onMouseDown={saveSelection}
                            onChange={(e) => { setFonteBarra(e.target.value); execFormat('fontName', e.target.value); }}>
                            {FONTES.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>

                        <select className="pef-select pef-select-size" title="Tamanho" value={tamanhoBarra}
                            onMouseDown={saveSelection}
                            onChange={(e) => { setTamanhoBarra(e.target.value); execFormat('fontSize', e.target.value); }}>
                            <option value="1">8pt</option>
                            <option value="2">10pt</option>
                            <option value="3">12pt</option>
                            <option value="4">14pt</option>
                            <option value="5">18pt</option>
                            <option value="6">24pt</option>
                            <option value="7">36pt</option>
                        </select>

                        <div className="pef-divider" />

                        <button className="pef-btn pef-bold" title="Negrito"
                            onMouseDown={(e) => { e.preventDefault(); execFormat('bold'); }}>B</button>
                        <button className="pef-btn pef-italic" title="Italico"
                            onMouseDown={(e) => { e.preventDefault(); execFormat('italic'); }}>I</button>
                        <button className="pef-btn pef-underline" title="Sublinhado"
                            onMouseDown={(e) => { e.preventDefault(); execFormat('underline'); }}>U</button>

                        <div className="pef-divider" />

                        <label className="pef-color-label" title="Cor do texto" onMouseDown={(e) => { e.preventDefault(); saveSelection(); }}>
                            <span className="pef-color-circle" style={{ background: headerTextColor }} />
                            <input type="color" className="pef-color-input" value={headerTextColor}
                                onChange={(e) => { setHeaderTextColor(e.target.value); execFormat('foreColor', e.target.value); }} />
                        </label>

                        {avisoFormatacao && <span className="pef-aviso">{avisoFormatacao}</span>}
                    </div>

                    {/* Canvas */}
                    <div className="prompt-email-canvas"
                        onDrop={handleCanvasDrop}
                        onDragOver={(e) => e.preventDefault()}
                        onClick={() => { setSelectedBlockId(null); }}
                    >
                        <div className="prompt-email-canvas-inner" onClick={(e) => e.stopPropagation()}>
                            {blocks.map((block, index) => renderBlock(block, index))}
                            <button className="prompt-email-add-btn" onClick={() => addBlock(BLOCK_TYPES.PARAGRAFO)}>+</button>
                        </div>
                    </div>
                </div>
            </div>
            {showExitPopup && (
                <div className="pef-exit-overlay">
                    <div className="pef-exit-popup">
                        <p className="pef-exit-msg">Tem certeza que deseja sair sem salvar as alteracoes?</p>
                        <div className="pef-exit-btns">
                            <button className="pef-exit-cancel" onClick={() => setShowExitPopup(false)}>Cancelar</button>
                            <button className="pef-exit-confirm" onClick={() => { setShowExitPopup(false); if (onClose) onClose(); }}>Sair</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Popup Carregar Template */}
            {showLoadPopup && (
                <div className="pef-exit-overlay" onClick={() => setShowLoadPopup(false)}>
                    <div className="pef-exit-popup" onClick={(e) => e.stopPropagation()} style={{ minWidth: 400, maxWidth: 600, maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <h3 style={{ margin: 0, color: '#fff', fontSize: 18 }}>Carregar Template</h3>
                            <button onClick={() => setShowLoadPopup(false)} style={{ background: 'none', border: 'none', color: '#aaa', fontSize: 22, cursor: 'pointer' }}>×</button>
                        </div>
                        {loadingTemplates ? (
                            <p style={{ color: '#aaa', textAlign: 'center' }}>Carregando templates...</p>
                        ) : savedTemplates.length === 0 ? (
                            <p style={{ color: '#aaa', textAlign: 'center' }}>Nenhum template salvo ainda.</p>
                        ) : (
                            <div style={{ overflowY: 'auto', flex: 1 }}>
                                {savedTemplates.map(t => {
                                    const tId = t.id || t.Id;
                                    const tTitulo = t.titulo || t.Titulo || 'Sem título';
                                    const tData = t.dataHora || t.DataHora;
                                    return (
                                        <div
                                            key={tId}
                                            onClick={() => handleSelectTemplate(t)}
                                            style={{
                                                padding: '14px 16px',
                                                borderBottom: '2px solid #000',
                                                cursor: 'pointer',
                                                transition: 'background 0.2s',
                                                borderRadius: 0,
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(235,133,0,0.15)'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                        >
                                            <div style={{ color: '#000', fontWeight: 600, fontSize: 15 }}>{tTitulo}</div>
                                            {tData && <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>{new Date(tData).toLocaleDateString('pt-BR')} {new Date(tData).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div>}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default PromptEmailEditor;
